/**
 * constants/role.ts · 13 角色 + roleHome 映射
 *
 * 设计原则:
 *   - 单一来源 · 所有角色 ID 在此
 *   - 14 个角色 home (含仓储子角色)
 *   - 与 department.ts 关联
 */

/** 13 个系统角色 (一级) */
export const Role = {
  ADMIN: 'admin',           // 管理员 (gm 同级)
  GM: 'gm',                 // 总经理
  BOSS: 'boss',             // 副总/老板
  SALES: 'sales',           // 销售
  ENGINEER: 'engineer',     // 工程师
  PURCHASING: 'purchasing', // 采购
  WAREHOUSE: 'warehouse',   // 仓储 (含 5 子角色见 ROLE_BY_USER_CODE)
  SUPERVISOR: 'supervisor', // 生产班长
  WORKER: 'worker',         // 工人
  QUALITY: 'quality',       // 品控
  MAINTENANCE: 'maintenance', // 维修
  FINANCE: 'finance',       // 财务
  HR: 'hr',                 // 人事
  GUARD: 'guard',           // 保卫
  ISO: 'iso',               // ISO
} as const;

export type RoleValue = typeof Role[keyof typeof Role];

/** 角色标签 */
export const RoleLabel: Record<RoleValue, { zh: string; vi: string; en: string }> = {
  admin:       { zh: '管理员',     vi: 'Quản trị viên',  en: 'Admin' },
  gm:          { zh: '总经理',     vi: 'Tổng giám đốc',  en: 'GM' },
  boss:        { zh: '老板',       vi: 'Chủ tịch',       en: 'Boss' },
  sales:       { zh: '销售',       vi: 'Kinh doanh',     en: 'Sales' },
  engineer:    { zh: '工程师',     vi: 'Kỹ sư',          en: 'Engineer' },
  purchasing:  { zh: '采购',       vi: 'Mua hàng',       en: 'Purchasing' },
  warehouse:   { zh: '仓管',       vi: 'Thủ kho',        en: 'Warehouse' },
  supervisor:  { zh: '班长',       vi: 'Tổ trưởng',      en: 'Supervisor' },
  worker:      { zh: '工人',       vi: 'Công nhân',      en: 'Worker' },
  quality:     { zh: '品控',       vi: 'QC',             en: 'Quality' },
  maintenance: { zh: '维修',       vi: 'Bảo trì',        en: 'Maintenance' },
  finance:     { zh: '财务',       vi: 'Kế toán',        en: 'Finance' },
  hr:          { zh: '人事',       vi: 'Nhân sự',        en: 'HR' },
  guard:       { zh: '保卫',       vi: 'Bảo vệ',         en: 'Guard' },
  iso:         { zh: 'ISO 管理员', vi: 'ISO',            en: 'ISO' },
};

/**
 * 角色 → home 路由
 *
 * 仓储有 3 个子 home (按 sub_role / user_code 决定):
 *   - WH-01 (主管) → warehouseHome
 *   - WH-02/04 (原料仓) → warehouseRawHome
 *   - WH-03/05 (成品仓) → warehouseFinishedHome
 */
export const ROLE_HOME: Record<RoleValue, string> = {
  admin: 'gmHome',
  gm: 'gmHome',
  boss: 'gmHome',
  sales: 'salesHome',
  engineer: 'engineerHome',
  purchasing: 'purchasingHome',
  warehouse: 'warehouseHome', // 默认 · 主管入口 · 子仓由 getHomeByUserCode() 处理
  supervisor: 'supervisorHome',
  worker: 'workerHome',
  quality: 'qualityHome',
  maintenance: 'maintHome',
  finance: 'financeHome',
  hr: 'hrHome',
  guard: 'guardHome',
  iso: 'isoHome',
};

/**
 * 按 user_code 智能 home (处理仓储子部门)
 *
 * 例: WH-02 / WH-04 → warehouseRawHome (原料仓)
 *     WH-03 / WH-05 → warehouseFinishedHome (成品仓)
 *     WH-01         → warehouseHome (主管总览)
 *     其他角色      → ROLE_HOME[role]
 */
export function getHomeByUserCode(userCode: string, role: RoleValue): string {
  if (role === 'warehouse') {
    if (userCode === 'WH-02' || userCode === 'WH-04') return 'warehouseRawHome';
    if (userCode === 'WH-03' || userCode === 'WH-05') return 'warehouseFinishedHome';
    return 'warehouseHome'; // WH-01 主管
  }
  return ROLE_HOME[role] || 'gmHome';
}

/** 管理层角色 (全权访问) */
export const ADMIN_ROLES: RoleValue[] = [Role.ADMIN, Role.GM, Role.BOSS];

export function isAdmin(role: RoleValue): boolean {
  return ADMIN_ROLES.includes(role);
}
